import {useEffect, useId, useState} from "react";
import {css} from "@panda/css";

import {Text} from "@pg-design/text-module";
import {REGISTRATION_WIZARD_TEST_IDS} from "@pg-mono/test-ids";
import {getTimeInMinutes} from "@pg-mono/user";
import {useUserDevice} from "@pg-mono/user-device";

import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {IAmplitudePhoneVerificationContext, trackAmplitudePhoneVerified} from "../../../user/tracking/track_amplitude_phone_verified";
import {HTTP_STATUS_TOO_MANY_REQUESTS} from "../../constants/http_status_too_many_requests";
import {useIsRegistrationWizardSmsLogin} from "../../hooks/use_is_registration_wizard_sms_login";
import {IRegistrationWizardSmsCodeError, useRegistrationWizardSmsCode} from "../../hooks/use_registration_wizard_sms_code";
import {useRegistrationWizardTrackingContext} from "../../hooks/use_registration_wizard_tracking_context";
import {setStep} from "../../state/registration_flow_slice";
import {trackRegistrationWizardButtonClick} from "../../tracking/track_registration_wizard_events";
import {RegistrationFlowStep} from "../../types/RegistrationFlowStep";
import {getMaskedPhone, getProfilePhoneValue, getSmsLoginPhoneString} from "../../utils/registration_wizard_phone";
import {getSmsCodeCooldownRemainingSeconds} from "../../utils/registration_wizard_sms_cooldown";
import {RegistrationWizardHotlineInfo} from "./RegistrationWizardHotlineInfo";
import {RegistrationWizardSmsCodeInputs, SMS_CODE_LENGTH} from "./RegistrationWizardSmsCodeInputs";
import {RegistrationWizardStepLayout} from "./RegistrationWizardStepLayout";

const EMPTY_DIGITS = Array.from({length: SMS_CODE_LENGTH}, () => "");

const hasFinePointer = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: fine)").matches;

interface IProps {
    titleId: string;
    amplitudeContext: IAmplitudePhoneVerificationContext;
    onVerified: (isSmsLogin: boolean) => Promise<void>;
    onBusyChange: (isBusy: boolean) => void;
}

export const RegistrationWizardSmsStep = (props: IProps) => {
    const {titleId, amplitudeContext, onVerified, onBusyChange} = props;
    const dispatch = useAppDispatch();
    const {isMobile} = useUserDevice();
    const {wizardDrafts} = useAppSelector((state) => state.registrationFlow);
    const isSmsLogin = useIsRegistrationWizardSmsLogin();
    const {data: userProfile} = useGetUserProfileQuery();
    const trackingContext = useRegistrationWizardTrackingContext();
    const errorId = useId();
    const smsLoginPhone = wizardDrafts.smsLoginPhone ?? wizardDrafts.phone;
    const smsLoginCredentials =
        isSmsLogin && wizardDrafts.email && smsLoginPhone ? {email: wizardDrafts.email, phone: getSmsLoginPhoneString(smsLoginPhone)} : null;
    const phone = isSmsLogin ? smsLoginPhone : (getProfilePhoneValue(userProfile?.phone) ?? wizardDrafts.phone);
    const {remainingSeconds, sendCode, verifyCode} = useRegistrationWizardSmsCode(smsLoginCredentials);
    const [digits, setDigits] = useState<string[]>(EMPTY_DIGITS);
    const [error, setError] = useState<IRegistrationWizardSmsCodeError | null>(null);
    const [errorCount, setErrorCount] = useState(0);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [focusRequestId, setFocusRequestId] = useState(0);
    const isResendBlocked = error?.status === HTTP_STATUS_TOO_MANY_REQUESTS;

    const setVerifying = (isBusy: boolean) => {
        setIsVerifying(isBusy);
        onBusyChange(isBusy);
    };

    const showError = (nextError: IRegistrationWizardSmsCodeError | null) => {
        setError(nextError);

        if (nextError) {
            setErrorCount((count) => count + 1);
        }
    };

    const send = async () => {
        setIsSending(true);
        const sendError = await sendCode();
        setIsSending(false);
        showError(sendError);

        return sendError;
    };

    const resend = async () => {
        const sendError = await send();

        if (!sendError) {
            setFocusRequestId((requestId) => requestId + 1);
        }
    };

    useEffect(() => {
        if (isSmsLogin && !smsLoginCredentials) {
            dispatch(setStep({step: RegistrationFlowStep.PHONE_COMPLETION}));
            return;
        }

        if (!isSmsLogin && getSmsCodeCooldownRemainingSeconds("profile") === 0) {
            void send();
        }
    }, []);

    const onResend = () => {
        if (remainingSeconds > 0 || isSending || isVerifying) {
            return;
        }

        trackRegistrationWizardButtonClick(trackingContext, "rp_auth_resend_sms");
        setDigits(EMPTY_DIGITS);
        void resend();
    };

    const onComplete = async (code: string) => {
        if (isVerifying) {
            return;
        }

        setVerifying(true);
        const verifyError = await verifyCode(code);

        if (verifyError) {
            trackAmplitudePhoneVerified(false, amplitudeContext);
            setVerifying(false);
            showError(verifyError);
            setDigits(EMPTY_DIGITS);
            setFocusRequestId((requestId) => requestId + 1);
            return;
        }

        trackAmplitudePhoneVerified(true, amplitudeContext);
        setError(null);

        try {
            await onVerified(isSmsLogin);
        } finally {
            setVerifying(false);
        }
    };

    const renderResend = () => {
        if (isResendBlocked) {
            return <RegistrationWizardHotlineInfo />;
        }

        return (
            <>
                Nie dostałeś kodu?{" "}
                {remainingSeconds > 0 ? (
                    <span>Wyślij ponownie za {getTimeInMinutes(remainingSeconds)}</span>
                ) : (
                    <button
                        type="button"
                        className={resendButtonStyle}
                        onClick={onResend}
                        disabled={isSending || isVerifying}
                        data-testid={REGISTRATION_WIZARD_TEST_IDS.SMS_RESEND_BUTTON}
                    >
                        Wyślij ponownie
                    </button>
                )}
            </>
        );
    };

    return (
        <RegistrationWizardStepLayout
            titleId={titleId}
            title="Wpisz kod z SMS"
            description={
                <>
                    Wpisz 4-cyfrowy kod aktywacyjny, który wysłaliśmy na Twój numer telefonu{" "}
                    {phone && <span className={maskedPhoneStyle}>{getMaskedPhone(phone)}.</span>}
                </>
            }
        >
            <div className={codeSectionStyle}>
                <RegistrationWizardSmsCodeInputs
                    digits={digits}
                    hasError={Boolean(error) && !isResendBlocked}
                    isBusy={isVerifying}
                    autoFocus={isMobile === false && hasFinePointer()}
                    focusRequestId={focusRequestId}
                    describedById={error ? errorId : undefined}
                    onChange={setDigits}
                    onComplete={(code) => void onComplete(code).catch(() => undefined)}
                />

                {error && (
                    <div role="alert">
                        <Text
                            key={errorCount}
                            as="p"
                            variant="info_txt_1"
                            id={errorId}
                            className={errorStyle}
                            dataTestId={REGISTRATION_WIZARD_TEST_IDS.SMS_ERROR}
                        >
                            {error.message}
                        </Text>
                    </div>
                )}

                <Text as="p" variant="info_txt_1" className={resendStyle}>
                    {renderResend()}
                </Text>
            </div>
        </RegistrationWizardStepLayout>
    );
};

const codeSectionStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5
});

const maskedPhoneStyle = css({
    color: "var(--colors-secondary)",
    whiteSpace: "nowrap"
});

const errorStyle = css({
    color: "var(--colors-danger)"
});

const resendStyle = css({
    py: 1.5,
    textAlign: "center",
    color: "var(--colors-gray-700)"
});

const resendButtonStyle = css({
    p: 0,
    border: "none",
    background: "none",
    color: "inherit",
    font: "inherit",
    textDecoration: "underline",
    cursor: "pointer"
});
import {useEffect, useId, useState} from "react";
import {css} from "@panda/css";

import {Text} from "@pg-design/text-module";
import {REGISTRATION_WIZARD_TEST_IDS} from "@pg-mono/test-ids";
import {getTimeInMinutes} from "@pg-mono/user";
import {useUserDevice} from "@pg-mono/user-device";

import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {IAmplitudePhoneVerificationContext, trackAmplitudePhoneVerified} from "../../../user/tracking/track_amplitude_phone_verified";
import {HTTP_STATUS_TOO_MANY_REQUESTS} from "../../constants/http_status_too_many_requests";
import {useIsRegistrationWizardSmsLogin} from "../../hooks/use_is_registration_wizard_sms_login";
import {IRegistrationWizardSmsCodeError, useRegistrationWizardSmsCode} from "../../hooks/use_registration_wizard_sms_code";
import {useRegistrationWizardTrackingContext} from "../../hooks/use_registration_wizard_tracking_context";
import {setStep} from "../../state/registration_flow_slice";
import {trackRegistrationWizardButtonClick} from "../../tracking/track_registration_wizard_events";
import {RegistrationFlowStep} from "../../types/RegistrationFlowStep";
import {getMaskedPhone, getProfilePhoneValue, getSmsLoginPhoneString} from "../../utils/registration_wizard_phone";
import {getSmsCodeCooldownRemainingSeconds} from "../../utils/registration_wizard_sms_cooldown";
import {RegistrationWizardHotlineInfo} from "./RegistrationWizardHotlineInfo";
import {RegistrationWizardSmsCodeInputs, SMS_CODE_LENGTH} from "./RegistrationWizardSmsCodeInputs";
import {RegistrationWizardStepLayout} from "./RegistrationWizardStepLayout";

const EMPTY_DIGITS = Array.from({length: SMS_CODE_LENGTH}, () => "");

const hasFinePointer = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: fine)").matches;

interface IProps {
    titleId: string;
    amplitudeContext: IAmplitudePhoneVerificationContext;
    onVerified: (isSmsLogin: boolean) => Promise<void>;
    onBusyChange: (isBusy: boolean) => void;
}

export const RegistrationWizardSmsStep = (props: IProps) => {
    const {titleId, amplitudeContext, onVerified, onBusyChange} = props;
    const dispatch = useAppDispatch();
    const {isMobile} = useUserDevice();
    const {wizardDrafts} = useAppSelector((state) => state.registrationFlow);
    const isSmsLogin = useIsRegistrationWizardSmsLogin();
    const {data: userProfile} = useGetUserProfileQuery();
    const trackingContext = useRegistrationWizardTrackingContext();
    const errorId = useId();
    const smsLoginPhone = wizardDrafts.smsLoginPhone ?? wizardDrafts.phone;
    const smsLoginCredentials =
        isSmsLogin && wizardDrafts.email && smsLoginPhone ? {email: wizardDrafts.email, phone: getSmsLoginPhoneString(smsLoginPhone)} : null;
    const phone = isSmsLogin ? smsLoginPhone : (getProfilePhoneValue(userProfile?.phone) ?? wizardDrafts.phone);
    const {remainingSeconds, sendCode, verifyCode} = useRegistrationWizardSmsCode(smsLoginCredentials);
    const [digits, setDigits] = useState<string[]>(EMPTY_DIGITS);
    const [error, setError] = useState<IRegistrationWizardSmsCodeError | null>(null);
    const [errorCount, setErrorCount] = useState(0);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [focusRequestId, setFocusRequestId] = useState(0);
    const isResendBlocked = error?.status === HTTP_STATUS_TOO_MANY_REQUESTS;

    const setVerifying = (isBusy: boolean) => {
        setIsVerifying(isBusy);
        onBusyChange(isBusy);
    };

    const showError = (nextError: IRegistrationWizardSmsCodeError | null) => {
        setError(nextError);

        if (nextError) {
            setErrorCount((count) => count + 1);
        }
    };

    const send = async () => {
        setIsSending(true);
        const sendError = await sendCode();
        setIsSending(false);
        showError(sendError);

        return sendError;
    };

    const resend = async () => {
        const sendError = await send();

        if (!sendError) {
            setFocusRequestId((requestId) => requestId + 1);
        }
    };

    useEffect(() => {
        if (isSmsLogin && !smsLoginCredentials) {
            dispatch(setStep({step: RegistrationFlowStep.PHONE_COMPLETION}));
            return;
        }

        if (!isSmsLogin && getSmsCodeCooldownRemainingSeconds("profile") === 0) {
            void send();
        }
    }, []);

    const onResend = () => {
        if (remainingSeconds > 0 || isSending || isVerifying) {
            return;
        }

        trackRegistrationWizardButtonClick(trackingContext, "rp_auth_resend_sms");
        setDigits(EMPTY_DIGITS);
        void resend();
    };

    const onComplete = async (code: string) => {
        if (isVerifying) {
            return;
        }

        setVerifying(true);
        const verifyError = await verifyCode(code);

        if (verifyError) {
            trackAmplitudePhoneVerified(false, amplitudeContext);
            setVerifying(false);
            showError(verifyError);
            setDigits(EMPTY_DIGITS);
            setFocusRequestId((requestId) => requestId + 1);
            return;
        }

        trackAmplitudePhoneVerified(true, amplitudeContext);
        setError(null);

        try {
            await onVerified(isSmsLogin);
        } finally {
            setVerifying(false);
        }
    };

    const renderResend = () => {
        if (isResendBlocked) {
            return <RegistrationWizardHotlineInfo />;
        }

        return (
            <>
                Nie dostałeś kodu?{" "}
                {remainingSeconds > 0 ? (
                    <span>Wyślij ponownie za {getTimeInMinutes(remainingSeconds)}</span>
                ) : (
                    <button
                        type="button"
                        className={resendButtonStyle}
                        onClick={onResend}
                        disabled={isSending || isVerifying}
                        data-testid={REGISTRATION_WIZARD_TEST_IDS.SMS_RESEND_BUTTON}
                    >
                        Wyślij ponownie
                    </button>
                )}
            </>
        );
    };

    return (
        <RegistrationWizardStepLayout
            titleId={titleId}
            title="Wpisz kod z SMS"
            description={
                <>
                    Wpisz 4-cyfrowy kod aktywacyjny, który wysłaliśmy na Twój numer telefonu{" "}
                    {phone && <span className={maskedPhoneStyle}>{getMaskedPhone(phone)}.</span>}
                </>
            }
        >
            <div className={codeSectionStyle}>
                <RegistrationWizardSmsCodeInputs
                    digits={digits}
                    hasError={Boolean(error) && !isResendBlocked}
                    isBusy={isVerifying}
                    autoFocus={isMobile === false && hasFinePointer()}
                    focusRequestId={focusRequestId}
                    describedById={error ? errorId : undefined}
                    onChange={setDigits}
                    onComplete={(code) => void onComplete(code).catch(() => undefined)}
                />

                {error && (
                    <div role="alert">
                        <Text
                            key={errorCount}
                            as="p"
                            variant="info_txt_1"
                            id={errorId}
                            className={errorStyle}
                            dataTestId={REGISTRATION_WIZARD_TEST_IDS.SMS_ERROR}
                        >
                            {error.message}
                        </Text>
                    </div>
                )}

                <Text as="p" variant="info_txt_1" className={resendStyle}>
                    {renderResend()}
                </Text>
            </div>
        </RegistrationWizardStepLayout>
    );
};

const codeSectionStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5
});

const maskedPhoneStyle = css({
    color: "var(--colors-secondary)",
    whiteSpace: "nowrap"
});

const errorStyle = css({
    color: "var(--colors-danger)"
});

const resendStyle = css({
    py: 1.5,
    textAlign: "center",
    color: "var(--colors-gray-700)"
});

const resendButtonStyle = css({
    p: 0,
    border: "none",
    background: "none",
    color: "inherit",
    font: "inherit",
    textDecoration: "underline",
    cursor: "pointer"
});
